import React, { useState, useEffect } from 'react'
import { EventFormModalMetaProps, Event, Volunteer, EventLead } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { LeadAssignModal } from './LeadAssignModal'
import { LeadAssignModalMeta } from '../data'
import { DeleteIcon, EmailIcon, PersonIcon, PhoneIcon } from '../icons'
import { toDateInputValue } from '../utils/dates'

type LeadFormData = {
    volunteer: Volunteer
    role: string
    walkie_talkie: boolean
}

type Props = {
    meta: EventFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (eventData: any) => Promise<void>
    initialEvent?: Event | null
    canManageVisibility?: boolean
}

export const EventFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialEvent, canManageVisibility = false } = props

    const [isLeadAssignModalOpen, setIsLeadAssignModalOpen] = useState(false);
    const [editingLeadIndex, setEditingLeadIndex] = useState<number | null>(null);
    const [leads, setLeads] = useState<LeadFormData[]>([]);

    const [formData, setFormData] = useState({
        name: '',
        organizer: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        location: '',
        address: '',
        city: '',
        country: '',
        required_volunteer_count: 0,
        shifts_visible: false,
        include_in_duplicate_checks: true
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when editing an event
    useEffect(() => {
        if (initialEvent && isOpen) {
            setFormData({
                name: initialEvent.name ?? '',
                organizer: initialEvent.organizer ?? '',
                start_date: toDateInputValue(initialEvent.start_date),
                end_date: toDateInputValue(initialEvent.end_date),
                start_time: initialEvent.start_time ?? '',
                end_time: initialEvent.end_time ?? '',
                location: initialEvent.location ?? '',
                address: initialEvent.address ?? '',
                city: initialEvent.city ?? '',
                country: initialEvent.country ?? '',
                required_volunteer_count: initialEvent.required_volunteer_count ?? 0,
                shifts_visible: initialEvent.shifts_visible ?? false,
                include_in_duplicate_checks: initialEvent.include_in_duplicate_checks ?? true
            })

            // Load existing leads
            if (initialEvent.event_leads && initialEvent.event_leads.length > 0) {
                setLeads(initialEvent.event_leads.map(el => ({
                    volunteer: el.volunteer!,
                    role: el.role || '',
                    walkie_talkie: el.walkie_talkie || false
                })))
            } else {
                setLeads([])
            }
        } else if (!isOpen) {
            setFormData({
                name: '',
                organizer: '',
                start_date: '',
                end_date: '',
                start_time: '',
                end_time: '',
                location: '',
                address: '',
                city: '',
                country: '',
                required_volunteer_count: 0,
                shifts_visible: false,
                include_in_duplicate_checks: true
            })
            setLeads([])
        }

        setError(null)
    }, [initialEvent, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target
        const checked = (e.target as HTMLInputElement).checked
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            // Format time fields to ensure they're in HH:MM format (remove seconds if present)
            const formattedData = {
                ...formData,
                start_time: formData.start_time.substring(0, 5), // Ensure HH:MM format
                end_time: formData.end_time.substring(0, 5), // Ensure HH:MM format
                required_volunteer_count: Number(formData.required_volunteer_count),
                shifts_visible: formData.shifts_visible,
                include_in_duplicate_checks: formData.include_in_duplicate_checks,
                leads: leads.map(lead => ({
                    volunteer_csid: lead.volunteer.csid,
                    role: lead.role.trim() || null,
                    walkie_talkie: lead.walkie_talkie
                }))
            }

            await onSubmit(formattedData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const handleLeadModalOpen = (index: number | null = null) => {
        setEditingLeadIndex(index)
        setIsLeadAssignModalOpen(true)
    }

    const handleLeadModalClose = () => {
        setEditingLeadIndex(null)
        setIsLeadAssignModalOpen(false)
    }

    const handleLeadModalSubmit = async (volunteer: Volunteer) => {
        if (editingLeadIndex !== null) {
            // Update existing lead volunteer
            setLeads(prev => prev.map((lead, idx) =>
                idx === editingLeadIndex ? { ...lead, volunteer } : lead
            ))
        } else {
            // Add new lead
            setLeads(prev => [...prev, { volunteer, role: '', walkie_talkie: false }])
        }
        handleLeadModalClose()
    }

    const handleRemoveLead = (index: number) => {
        setLeads(prev => prev.filter((_, idx) => idx !== index))
    }

    const handleLeadRoleChange = (index: number, role: string) => {
        setLeads(prev => prev.map((lead, idx) =>
            idx === index ? { ...lead, role } : lead
        ))
    }

    const handleLeadWalkieTalkieChange = (index: number, walkie_talkie: boolean) => {
        setLeads(prev => prev.map((lead, idx) =>
            idx === index ? { ...lead, walkie_talkie } : lead
        ))
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title}
                maxWidth="2xl"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.cancelButton}
                        </Button>
                        <Button
                            type="submit"
                            form="event-form"
                            disabled={loading}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    </div>
                }
            >
                <form id="event-form" onSubmit={handleSubmit}>
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        {error && (
                            <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4">
                            {/* Event Name */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.nameLabel} <span className="text-danger-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.namePlaceholder}
                                />
                            </div>

                            {/* Organizer */}
                            <div>
                                <label htmlFor="organizer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.organizerLabel} <span className="text-danger-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="organizer"
                                    name="organizer"
                                    required
                                    value={formData.organizer}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.organizerPlaceholder}
                                />
                            </div>

                            {/* Date Range */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.startDateLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="start_date"
                                        name="start_date"
                                        required
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.endDateLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="end_date"
                                        name="end_date"
                                        required
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                            </div>

                            {/* Time Range */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.startTimeLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        id="start_time"
                                        name="start_time"
                                        required
                                        value={formData.start_time}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.endTimeLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        id="end_time"
                                        name="end_time"
                                        required
                                        value={formData.end_time}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    />
                                </div>
                            </div>

                            {/* Location */}
                            <div>
                                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.locationLabel} <span className="text-danger-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    required
                                    value={formData.location}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.locationPlaceholder}
                                />
                            </div>

                            {/* Address */}
                            <div>
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.addressLabel} <span className="text-danger-500">*</span>
                                </label>
                                <textarea
                                    id="address"
                                    name="address"
                                    required
                                    rows={2}
                                    value={formData.address}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                    placeholder={meta.addressPlaceholder}
                                />
                            </div>

                            {/* City and Country */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.cityLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        required
                                        value={formData.city}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                        placeholder={meta.cityPlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.countryLabel} <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="country"
                                        name="country"
                                        required
                                        value={formData.country}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                        placeholder={meta.countryPlaceholder}
                                    />
                                </div>
                                {/* <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.required_volunteer_count_title}
                                    </label>
                                    <input
                                        type="number"
                                        id="required_volunteer_count"
                                        name="required_volunteer_count"
                                        value={formData.required_volunteer_count}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                        placeholder={"0"}
                                    />
                                </div> */}
                            </div>

                            {/* Shifts Visible Toggle - only shown to admins */}
                            {canManageVisibility && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            id="shifts_visible"
                                            name="shifts_visible"
                                            checked={formData.shifts_visible}
                                            onChange={handleChange}
                                            className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                        />
                                        <div>
                                            <label htmlFor="shifts_visible" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {meta.shiftsVisibleLabel}
                                            </label>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {meta.shiftsVisibleDescription}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Include in Duplicate Checks Toggle - only shown to admins */}
                            {canManageVisibility && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            id="include_in_duplicate_checks"
                                            name="include_in_duplicate_checks"
                                            checked={formData.include_in_duplicate_checks}
                                            onChange={handleChange}
                                            className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                        />
                                        <div>
                                            <label htmlFor="include_in_duplicate_checks" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {meta.includeInDuplicateChecksLabel}
                                            </label>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {meta.includeInDuplicateChecksDescription}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Event Leads Section */}
                            <div className="w-full">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Event Leads
                                    </label>
                                    <Button
                                        type="button"
                                        color="success"
                                        size="sm"
                                        onClick={() => handleLeadModalOpen(null)}
                                    >
                                        + Add Lead
                                    </Button>
                                </div>

                                {leads.length === 0 && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-2">
                                        No leads assigned yet
                                    </p>
                                )}

                                <div className="space-y-3">
                                    {leads.map((lead, index) => (
                                        <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-white dark:bg-gray-800">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1 text-gray-900 dark:text-white">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <PersonIcon />
                                                        <p className="font-medium">{`${lead.volunteer.first_name} ${lead.volunteer.last_name}`}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                        <EmailIcon className="w-4 h-4" />
                                                        <p>{lead.volunteer.email}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                        <PhoneIcon className="w-4 h-4" />
                                                        <p>{lead.volunteer.phone}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleLeadModalOpen(index)}
                                                    >
                                                        Change
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="icon"
                                                        size="sm"
                                                        color="danger"
                                                        onClick={() => handleRemoveLead(index)}
                                                    >
                                                        <DeleteIcon className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                        Role (optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={lead.role}
                                                        onChange={(e) => handleLeadRoleChange(index, e.target.value)}
                                                        placeholder="e.g. Lead, Deputy, Logistics"
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                                    />
                                                </div>
                                                <div className="flex items-center pt-5">
                                                    <input
                                                        type="checkbox"
                                                        id={`walkie-talkie-${index}`}
                                                        checked={lead.walkie_talkie}
                                                        onChange={(e) => handleLeadWalkieTalkieChange(index, e.target.checked)}
                                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                                    />
                                                    <label htmlFor={`walkie-talkie-${index}`} className="ms-2 text-sm text-gray-600 dark:text-gray-400">
                                                        Requires Walkie Talkie
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </form>

            </Modal>

            <LeadAssignModal
                meta={LeadAssignModalMeta}
                title={editingLeadIndex !== null ? "Change Lead" : "Add Lead"}
                isOpen={isLeadAssignModalOpen}
                onClose={handleLeadModalClose}
                onSubmit={handleLeadModalSubmit}
                currentLead={editingLeadIndex !== null ? leads[editingLeadIndex]?.volunteer : null}
            />
        </>
    )
}
